import { useCallback, useEffect, useState } from "react";
import { Users, SearchCheck, FileText, Send, History, Settings } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { DashHeader } from "@/components/dash/DashHeader";
import { ProjectSwitcher } from "@/components/dash/ProjectSwitcher";
import { StatsRow } from "@/components/dash/StatsRow";
import { LeadsPanel } from "@/components/dash/LeadsPanel";
import { TemplatesPanel } from "@/components/dash/TemplatesPanel";
import { CampaignsPanel } from "@/components/dash/CampaignsPanel";
import { HistoryPanel } from "@/components/dash/HistoryPanel";
import { SettingsPanel } from "@/components/dash/SettingsPanel";
import { FinderPanel } from "@/components/dash/FinderPanel";

const TABS = [
  ["leads", "Leads", Users], ["finder", "Email Finder", SearchCheck], ["templates", "Templates", FileText],
  ["campaigns", "Campaigns", Send], ["history", "History", History], ["settings", "Settings", Settings],
];

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [slot, setSlot] = useState(Number(localStorage.getItem("slr_slot")) || 1);
  const [tab, setTab] = useState("leads");

  const loadProjects = useCallback(() => api.get("/projects").then((r) => setProjects(r.data)), []);
  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { localStorage.setItem("slr_slot", String(slot)); }, [slot]);

  const project = projects.find((p) => p.slot === slot);

  return (
    <div className="min-h-screen noise">
      <DashHeader />
      <main className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-8">
        <ProjectSwitcher projects={projects} slot={slot} onChange={setSlot} />
        {project && (
          <>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Project {slot}</p>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="active-project-name">{project.name}</h1>
                <p className="text-sm text-muted-foreground mt-1 font-mono" data-testid="active-project-sender">
                  {project.from_email ? `${project.from_name || project.name} <${project.from_email}>` : "No sender configured — set it in Settings"}
                </p>
              </div>
            </div>
            <StatsRow stats={project.stats} />
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-card border border-border h-auto min-h-11 p-1 flex-wrap justify-start">
                {TABS.map(([t, label, Icon]) => (
                  <TabsTrigger key={t} value={t} className="px-4 gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`tab-${t}`}>
                    <Icon className="h-4 w-4" />{label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="mt-6" key={slot}>
                <TabsContent value="leads"><LeadsPanel slot={slot} invalidCount={project.stats.invalid || 0} onChange={loadProjects} onFindEmails={() => setTab("finder")} /></TabsContent>
                <TabsContent value="finder"><FinderPanel slot={slot} onChange={loadProjects} /></TabsContent>
                <TabsContent value="templates"><TemplatesPanel slot={slot} project={project} onProjectChange={loadProjects} /></TabsContent>
                <TabsContent value="campaigns"><CampaignsPanel slot={slot} project={project} onChange={loadProjects} /></TabsContent>
                <TabsContent value="history"><HistoryPanel slot={slot} /></TabsContent>
                <TabsContent value="settings"><SettingsPanel project={project} onSaved={loadProjects} /></TabsContent>
              </div>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
